import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import config from "config";

const PARENT_ORIGIN_MESSAGE_TYPE = "PARENT_ORIGIN";
const CLIENT_CARD_URL_CHANGE_MESSAGE_TYPE = "CLIENT_CARD_URL_CHANGE";
const REQUEST_PARENT_ORIGIN_MESSAGE_TYPE = "REQUEST_PARENT_ORIGIN";
const CLIENT_CARD_URL_FROM_PARENT_MESSAGE_TYPE = "CLIENT_CARD_URL_FROM_PARENT";

const CLIENT_CARD_PATH_PATTERN = /^\/hub\/[^/]+\/clients\/[^/]+/;
/** Parent routes that embed the client-card iframe (modal client card or Work Desk). */
const WORKDESK_HUB_PATH_PATTERN = /^\/hub\/[^/]+\/workdesk(\/|$)/;

function isClientCardPath(pathname) {
  return CLIENT_CARD_PATH_PATTERN.test(pathname);
}

function isEmbeddedClientCardParentPath(pathname) {
  return isClientCardPath(pathname) || WORKDESK_HUB_PATH_PATTERN.test(pathname);
}

function getClientCardOrigin() {
  try {
    return new URL(config.clientCardUrl).origin;
  } catch {
    return null;
  }
}

const identityPath = (pathname) => {
  return pathname;
};

/**
 * @param {object} [options]
 * @param {(pathname: string) => string} [options.mapPathnameForIframeSrc]
 *   Map parent pathname for iframe URL (default: identity). Used for both the
 *   frozen boot src AND ongoing parent->iframe sync, unless
 *   `initialPathnameOverride` is also supplied.
 * @param {(pathname: string) => string} [options.mapPathnameFromChild]
 *   Map child pathname before history.replace (default: identity).
 * @param {string} [options.initialPathnameOverride]
 *   If supplied, overrides the frozen boot `iframeSrc` pathname. The ongoing
 *   parent->iframe sync still uses `mapPathnameForIframeSrc`. Use this when
 *   the iframe must boot at a specific URL regardless of the parent's current
 *   location (e.g. preloading).
 * @param {boolean} [options.syncEnabled=true]
 *   When false, bidirectional URL sync is suspended: the parent does not post
 *   `CLIENT_CARD_URL_FROM_PARENT` to the iframe, and the parent ignores
 *   `CLIENT_CARD_URL_CHANGE` from the iframe. The iframe stays mounted and
 *   warm; only URL sync pauses. When flipped back to true, the parent's
 *   current URL is posted to the iframe so it catches up.
 */
export function useClientCardIframeSync(options = {}) {
  const {
    mapPathnameForIframeSrc = identityPath,
    mapPathnameFromChild = identityPath,
    initialPathnameOverride,
    syncEnabled = true,
  } = options;
  const history = useHistory();
  const iframeRef = useRef(null);
  const location = useLocation();
  const skipNextParentToIframeSyncRef = useRef(false);

  // useState (lazy init): freeze src at mount so history.replace from iframe sync does not change src and reload the iframe.
  // Without this, iframe will flicker when navigating to a new url
  const [iframeSrc] = useState(() => {
    const pathname =
      typeof initialPathnameOverride === "string"
        ? initialPathnameOverride
        : mapPathnameForIframeSrc(location.pathname);
    const search =
      typeof initialPathnameOverride === "string" ? "" : location.search;

    return `${config.clientCardUrl}${pathname}${search}`;
  });

  const handleMessage = useCallback(
    (event) => {
      const clientCardOrigin = getClientCardOrigin();

      if (!clientCardOrigin || event.origin !== clientCardOrigin) return;
      if (
        event.data?.type === REQUEST_PARENT_ORIGIN_MESSAGE_TYPE &&
        event.source
      ) {
        event.source.postMessage(
          { type: PARENT_ORIGIN_MESSAGE_TYPE, origin: window.location.origin },
          event.origin
        );
        return;
      }
      if (event.data?.type !== CLIENT_CARD_URL_CHANGE_MESSAGE_TYPE) return;

      if (!syncEnabled) return;

      // Ignore iframe-initiated URL changes when the parent is not on an
      // embedded path. This prevents the preloaded iframe's internal
      // navigations (e.g. auto-selecting the first client on boot) from
      // yanking the user's browser URL while they're on an unrelated page.
      if (!isEmbeddedClientCardParentPath(location.pathname)) return;

      const { pathname, search } = event.data;
      if (typeof pathname !== "string") return;

      const searchStr = typeof search === "string" ? search : "";
      const mapped = mapPathnameFromChild(pathname);
      const url = mapped + searchStr;
      const current =
        location.pathname + (location.search ? location.search : "");
      if (url === current) return;

      skipNextParentToIframeSyncRef.current = true;
      history.replace(url);
    },
    [
      history,
      location.pathname,
      location.search,
      mapPathnameFromChild,
      syncEnabled,
    ]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [history, handleMessage]);

  useEffect(() => {
    if (skipNextParentToIframeSyncRef.current) {
      skipNextParentToIframeSyncRef.current = false;
      return;
    }

    if (!syncEnabled) return;

    const iframe = iframeRef.current;
    const clientCardOrigin = getClientCardOrigin();

    if (
      !iframe?.contentWindow ||
      !clientCardOrigin ||
      !isEmbeddedClientCardParentPath(location.pathname)
    ) {
      return;
    }

    iframe.contentWindow.postMessage(
      {
        type: CLIENT_CARD_URL_FROM_PARENT_MESSAGE_TYPE,
        pathname: mapPathnameForIframeSrc(location.pathname),
        search: location.search || "",
      },
      clientCardOrigin
    );
  }, [
    location.pathname,
    location.search,
    mapPathnameForIframeSrc,
    syncEnabled,
  ]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const clientCardOrigin = getClientCardOrigin();

    if (iframe?.contentWindow && clientCardOrigin) {
      iframe.contentWindow.postMessage(
        { type: PARENT_ORIGIN_MESSAGE_TYPE, origin: window.location.origin },
        clientCardOrigin
      );
      if (syncEnabled && isEmbeddedClientCardParentPath(location.pathname)) {
        iframe.contentWindow.postMessage(
          {
            type: CLIENT_CARD_URL_FROM_PARENT_MESSAGE_TYPE,
            pathname: mapPathnameForIframeSrc(location.pathname),
            search: location.search || "",
          },
          clientCardOrigin
        );
      }
    }
  }, [
    location.pathname,
    location.search,
    mapPathnameForIframeSrc,
    syncEnabled,
  ]);

  return { iframeRef, iframeSrc, handleIframeLoad };
}

export default useClientCardIframeSync;
